// src/features/businesses/userSlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  activeUserPerDay,
  ISuplierPagination,
  NonActiveUsersResults,
  StatsResults,
} from 'services/@types';
import StatsService from 'services/stats.api';
import InvitesService from 'services/invites.api';
export interface PermissionState {
  stats: StatsResults;
  activeUsers: activeUserPerDay[];
  loading: boolean;
  error: string | null;
  nonActiveUsers: NonActiveUsersResults;
  pendingInvites: ISuplierPagination;
}

const initialState: PermissionState = {
  loading: false,
  error: null,
  stats: {
    totalBusinesses: 0,
    monthlyRegisteredBusinesses: 0,
    dailyActiveUsers: 0,
    monthlyActiveUsers: 0,
    weeklyActiveUsers: 0,
    businessesByCategory: [],
    registeredSuppliers: 0,
    invitedSuppliers: 0,
  },
  activeUsers: [],
  nonActiveUsers: {
    total: 0,
    page: 1,
    limit: 6,
    results: [],
    totalPages: 0,
    daysAgo: 7,
  },
  pendingInvites: {
    totalResults: 0,
    page: 1,
    limit: 6,
    totalPages: 0,
    results: [],
  },
};

export const getStats = createAsyncThunk(
  'stats/getStats',
  async (_, { rejectWithValue }) => {
    try {
      const stats = await StatsService.getStats();
      return stats;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getActiveUsers = createAsyncThunk(
  'stats/getActiveUsers',
  async (daysAgo: number, { rejectWithValue }) => {
    try {
      const activeUsers = await StatsService.getActiveUsers(daysAgo);
      return activeUsers;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getNonActiveUsers = createAsyncThunk(
  'stats/getNonActiveUsers',
  async (params: { daysAgo: number; page: number; limit: number }) => {
    const nonActiveUsers = await StatsService.getNonActiveUsers(params);
    return nonActiveUsers;
  },
);

export const getPendingInvites = createAsyncThunk(
  'stats/getPendingInvites',
  async (params: { page: number; limit: number }) => {
    const pendingInvites = await InvitesService.getInvites(params);
    return pendingInvites;
  },
);

const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    setNonActiveParams: (state, action) => {
      state.nonActiveUsers = {
        ...state.nonActiveUsers,
        ...action.payload,
      };
    },
    setPendingInvitesParams: (state, action) => {
      state.pendingInvites = {
        ...state.pendingInvites,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStats.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.stats = action.payload;
      })
      .addCase(getStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getActiveUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.activeUsers = action.payload;
      })
      .addCase(getActiveUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getNonActiveUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNonActiveUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.nonActiveUsers = {
          ...state.nonActiveUsers,
          ...action.payload.results,
        };
      })
      .addCase(getNonActiveUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getPendingInvites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPendingInvites.fulfilled, (state, action) => {
        console.log(action.payload);
        state.loading = false;
        state.error = null;
        state.pendingInvites = {
          ...state.pendingInvites,
          ...action.payload.results,
        };
      })
      .addCase(getPendingInvites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setNonActiveParams, setPendingInvitesParams } =
  statsSlice.actions;

export default statsSlice.reducer;
